import { useState } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';

import OurServiceSectionStyle from './style';

import SmallRadiusButton from 'src/components/atoms/button/smallRadiusButton';
import IconCard from 'src/components/atoms/iconCard';
import Image from 'src/components/atoms/image';
import config from 'src/config';

type FadedInOutAnimatePropType = {
  open: boolean;
  children: object;
};

const FadedInOutAnimate = ({ open, children }: FadedInOutAnimatePropType) => {
  const styles = useSpring({ opacity: open ? 1 : 0, delay: 100 });
  return <animated.div style={{ width: '100%', ...styles, position: 'absolute' }}>{children}</animated.div>;
};

const OurServiceSection = () => {
  const { t } = useTranslation('business');

  const CARD_DATA = [
    {
      name: t('digital_learning_platform'),
      alt: t('digital_learning_platform_alt'),
      icon: 'digital-learning-platform.svg',
      content: t('digital_learning_platform_content'),
    },
    {
      name: t('on_demand_courses'),
      alt: t('on_demand_courses_alt'),
      icon: 'on-demand-courses.svg',
      content: t('on_demand_courses_content'),
    },
    {
      name: t('consulting_services'),
      alt: t('consulting_services_alt'),
      icon: 'consulting-services.svg',
      content: t('consulting_services_content'),
    },
    {
      name: t('course_development_services'),
      alt: t('course_development_services_alt'),
      icon: 'course-development-services.svg',
      content: t('course_development_services_content'),
    },
  ];

  const [skilllanePlusIsActive, setSkilllanePlusIsActive] = useState(true);
  const [tuxsaIsActive, setTuxsaIsActive] = useState(false);
  const [onwardIsActive, setOnwardIsActive] = useState(false);
  const [cpdIsActive, setCpdIsActive] = useState(false);
  const [tabIndex, setTabIndex] = useState(1);

  const clickSkilllanePlus = () => {
    setSkilllanePlusIsActive(true);
    setTuxsaIsActive(false);
    setOnwardIsActive(false);
    setCpdIsActive(false);
    setTabIndex(1);
  };

  const clickTuxsa = () => {
    setSkilllanePlusIsActive(false);
    setTuxsaIsActive(true);
    setOnwardIsActive(false);
    setCpdIsActive(false);
    setTabIndex(2);
  };
  const clickOnward = () => {
    setSkilllanePlusIsActive(false);
    setTuxsaIsActive(false);
    setOnwardIsActive(true);
    setCpdIsActive(false);
    setTabIndex(3);
  };
  const clickCpd = () => {
    setSkilllanePlusIsActive(false);
    setTuxsaIsActive(false);
    setOnwardIsActive(false);
    setCpdIsActive(true);
    setTabIndex(4);
  };

  return (
    <OurServiceSectionStyle>
      <div className="container">
        <Typography className="title-eng">{t('our_services_eng')}</Typography>
        <Typography className="title">{t('our_services')}</Typography>
        <Typography className="subtitle">{t('our_services_subtitle_1')}</Typography>
        <Typography className="subtitle">{t('our_services_subtitle_2')}</Typography>

        <Grid container spacing={1} padding={{ lg: '30px 0px', xs: '30px 0px 80px 0px' }}>
          {CARD_DATA.map((card) => (
            <Grid item lg={3} md={6} xs={12} display="flex" key={card.name}>
              <IconCard icon={card.icon} alt={card.alt} name={card.name} content={card.content} />
            </Grid>
          ))}
        </Grid>

        <Typography className="title-eng primary-dark-color">{t('skilllane_for_business')}</Typography>
        <Typography className="subtitle">{t('skilllane_for_business_subtitle')}</Typography>

        <Box className="skl-for-business-button-group">
          <SmallRadiusButton
            variant="contained"
            style={{ marginRight: '10px' }}
            // @ts-expect-error: ignore for using additional palette
            color={skilllanePlusIsActive ? 'primaryDark' : 'info'}
            onClick={clickSkilllanePlus}
          >
            SkillLane+
          </SmallRadiusButton>
          <SmallRadiusButton
            variant="contained"
            style={{ marginRight: '10px' }}
            // @ts-expect-error: ignore for using additional palette
            color={tuxsaIsActive ? 'primaryDark' : 'info'}
            onClick={clickTuxsa}
          >
            TUXSA
          </SmallRadiusButton>
          <SmallRadiusButton
            variant="contained"
            style={{ marginRight: '10px' }}
            // @ts-expect-error: ignore for using additional palette
            color={onwardIsActive ? 'primaryDark' : 'info'}
            onClick={clickOnward}
          >
            Onward
          </SmallRadiusButton>
          <SmallRadiusButton
            variant="contained"
            style={{ marginRight: '10px' }}
            // @ts-expect-error: ignore for using additional palette
            color={cpdIsActive ? 'primaryDark' : 'info'}
            onClick={clickCpd}
          >
            CPD
          </SmallRadiusButton>
        </Box>

        <Box className="skl-for-business-content">
          <FadedInOutAnimate open={tabIndex === 1}>
            <Grid container spacing={1} className="content-container">
              <Grid item lg={1} />
              <Grid item lg={4} md={12} xs={12} paddingRight="16px">
                <Box className="skilllane-plus-title">
                  <Image alt="SkillLane Plus" src="/static/icons/skilllane-plus-logo.svg" />
                </Box>
                <Typography fontSize={18} color="secondary.light">
                  {t('skilllane_plus_content')}
                </Typography>
              </Grid>
              <Grid item lg={6} md={12} xs={12} marginTop="15px">
                <Image
                  alt={t('skilllane_plus_alt')}
                  src={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/Skilllane%2B-image.png`}
                  // eslint-disable-next-line max-len
                  srcSet={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/Skilllane%2B-image.png, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/Skilllane%2B-imagex2.png 2x`}
                  // eslint-disable-next-line max-len
                  srcSetWebp={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/Skilllane%2B-image.webp, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/Skilllane%2B-imagex2.webp 2x`}
                />
              </Grid>
              <Grid item lg={1} />
            </Grid>
          </FadedInOutAnimate>
          <FadedInOutAnimate open={tabIndex === 2}>
            <Grid container spacing={1} className="content-container">
              <Grid item lg={1} />
              <Grid item lg={4} md={12} xs={12} paddingRight="16px">
                <Box className="tuxsa-title">
                  <Image alt="TUXSA" src="/static/icons/tuxsa-logo.svg" />
                </Box>
                <Typography fontSize={18} color="secondary.light">
                  {t('tuxsa_content')}
                </Typography>
              </Grid>
              <Grid item lg={6} md={12} xs={12} marginTop="15px">
                <Image
                  alt={t('tuxsa_alt')}
                  src={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/TUXSA-image.png`}
                  // eslint-disable-next-line max-len
                  srcSet={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/TUXSA-image.png, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/TUXSA-imagex2.png 2x`}
                  // eslint-disable-next-line max-len
                  srcSetWebp={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/TUXSA-image.webp, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/TUXSA-imagex2.webp 2x`}
                />
              </Grid>
              <Grid item lg={1} />
            </Grid>
          </FadedInOutAnimate>
          <FadedInOutAnimate open={tabIndex === 3}>
            <Grid container spacing={1} className="content-container">
              <Grid item lg={1} />
              <Grid item lg={4} md={12} xs={12} paddingRight="16px">
                <Box className="onward-title">
                  <Image alt="Onward" src="/static/icons/onward-logo.svg" />
                </Box>
                <Typography fontSize={18} color="secondary.light">
                  {t('onward_subtitle')}
                </Typography>
                <Grid container spacing={{ lg: 1, xs: 0 }}>
                  <Grid item lg={3} md={2} xs={4}>
                    <Typography fontSize={18} fontWeight="bold" color="primary.dark">
                      {t('onward_expand_title_1')}
                    </Typography>
                  </Grid>
                  <Grid item lg={9} md={10} xs={8} className="onward-expand-subtitle">
                    <Typography>{t('onward_expand_subtitle_1')}</Typography>
                  </Grid>
                  <Grid item lg={3} md={2} xs={4}>
                    <Typography fontSize={18} fontWeight="bold" color="primary.dark">
                      {t('onward_expand_title_2')}
                    </Typography>
                  </Grid>
                  <Grid item lg={9} md={10} xs={8} className="onward-expand-subtitle">
                    <Typography>{t('onward_expand_subtitle_2')}</Typography>
                  </Grid>
                  <Grid item lg={3} md={2} xs={4}>
                    <Typography fontSize={18} fontWeight="bold" color="primary.dark">
                      {t('onward_expand_title_3')}
                    </Typography>
                  </Grid>
                  <Grid item lg={9} md={10} xs={8} className="onward-expand-subtitle">
                    <Typography>{t('onward_expand_subtitle_3')}</Typography>
                  </Grid>
                  <Grid item lg={3} md={2} xs={4}>
                    <Typography fontSize={18} fontWeight="bold" color="primary.dark">
                      {t('onward_expand_title_4')}
                    </Typography>
                  </Grid>
                  <Grid item lg={9} md={10} xs={8} className="onward-expand-subtitle">
                    <Typography>{t('onward_expand_subtitle_4')}</Typography>
                  </Grid>
                  <Grid item lg={3} md={2} xs={4}>
                    <Typography fontSize={18} fontWeight="bold" color="primary.dark">
                      {t('onward_expand_title_5')}
                    </Typography>
                  </Grid>
                  <Grid item lg={9} md={10} xs={8} className="onward-expand-subtitle">
                    <Typography>{t('onward_expand_subtitle_5')}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item lg={6} md={12} xs={12} marginTop="15px">
                <Image
                  alt={t('cpd_alt')}
                  src={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/Onward-image.png`}
                  // eslint-disable-next-line max-len
                  srcSet={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/Onward-image.png, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/Onward-imagex2.png 2x`}
                  // eslint-disable-next-line max-len
                  srcSetWebp={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/Onward-image.webp, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/Onward-imagex2.webp 2x`}
                />
              </Grid>
              <Grid item lg={1} />
            </Grid>
          </FadedInOutAnimate>
          <FadedInOutAnimate open={tabIndex === 4}>
            <Grid container spacing={1} className="content-container">
              <Grid item lg={1} />
              <Grid item lg={4} md={12} xs={12} paddingRight="16px">
                <Box className="cpd-title">
                  <Image alt="SkillLaneCPD" src="/static/icons/cpd-logo.svg" />
                </Box>
                <Typography fontSize={18} color="secondary.light">
                  {t('cpd_content')}
                </Typography>
              </Grid>
              <Grid item lg={6} md={12} xs={12} marginTop="15px">
                <Image
                  alt={t('cpd_alt')}
                  src={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/CPD-image.png`}
                  // eslint-disable-next-line max-len
                  srcSet={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/CPD-image.png, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/CPD-imagex2.png 2x`}
                  // eslint-disable-next-line max-len
                  srcSetWebp={`${config.IMAGE_CDN_HOST}/assets/b2b-landing/CPD-image.webp, ${config.IMAGE_CDN_HOST}/assets/b2b-landing/CPD-imagex2.webp 2x`}
                />
              </Grid>
              <Grid item lg={1} />
            </Grid>
          </FadedInOutAnimate>
        </Box>
      </div>
    </OurServiceSectionStyle>
  );
};

export default OurServiceSection;
